/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import * as React from 'react';

import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import { css, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { Comment } from '../comment';
import { RequestFeedbackError } from '../request-feedback-error';
import { StarRating } from '../star-rating';
import messages from './messages';

interface RequestFeedbackFormProps {
    rating: number;
    commentSubmitting: boolean;
    onRatingChange: (rating: number) => void;
    onSaveComment: (comment: string) => void;
    validationErrorType?: string;
    onErrorFlagDismiss: () => void;
}

export class RequestFeedbackForm extends React.Component<RequestFeedbackFormProps & WrappedComponentProps> {
    getDescription = (rating: number) => {
        if (rating <= 0) {
            return messages.NoResultDescription;
        }
        if (rating < 3) {
            return messages.SadFaceDescription;
        }
        if (rating === 3) {
            return messages.NeutralFaceDescription;
        }
        return messages.HappyFaceDescription;
    };

    getCommentLabel = (rating: number) => {
        if (rating <= 0) {
            return messages.NoResultCommentLabel;
        }
        if (rating < 4) {
            return messages.SadFaceCommentLabel;
        }
        return messages.HappyFaceCommentLabel;
    };

    canSubmitComment = (rating: number) => {
        return rating >= 1 && rating <= 5;
    };

    render() {
        const {
            onSaveComment,
            onRatingChange,
            commentSubmitting,
            intl,
            rating,
            validationErrorType,
            onErrorFlagDismiss,
        } = this.props;
        const descriptionMessage = this.getDescription(rating);
        const commentLabel = this.getCommentLabel(rating);
        const canSubmitComment = this.canSubmitComment(rating);

        return (
            <>
                <FireScreenEventOnMount />
                <div css={starRatingContainerStyles}>
                    <StarRating value={rating} onChange={onRatingChange} commentSubmitting={commentSubmitting} />
                </div>
                <div css={textContainerStyles}>
                    {/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
                    <h3 data-test-i18n={descriptionMessage.id}>{intl.formatMessage(descriptionMessage)}</h3>
                    <div css={textCommentContainerStyles} data-test-i18n={commentLabel.id}>
                        {intl.formatMessage(commentLabel)}
                    </div>
                </div>
                <Comment onSaveComment={onSaveComment} canSubmit={canSubmitComment} isSubmitting={commentSubmitting} />
                {validationErrorType && (
                    <RequestFeedbackError errorType={validationErrorType} onErrorFlagDismiss={onErrorFlagDismiss} />
                )}
            </>
        );
    }
}

export default injectIntl(RequestFeedbackForm);

const starRatingContainerStyles = css({
    marginBottom: token('space.300', '24px'),
});

const textContainerStyles = css({
    marginBottom: token('space.600', '48px'),
});

const textCommentContainerStyles = css({
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginTop: token('space.100', '8px'),
});
